import { useEffect, useState } from "react";
import * as api from "./apiService";

export default function CustomerServiceSelection({ selected, onChange, retainer = false, required = true }) {
  const kind = retainer ? "retainer services" : "services";
  const sectionId = retainer ? "add-customer-retainers" : "add-customer-services";
  const searchId = `${sectionId}-search`;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [services, setServices] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await (retainer ? api.getRetainers : api.getServices)({ name: search }, page);
        if (active) {
          setServices(data.results);
          setHasNext(Boolean(data.next));
        }
      } catch {
        if (active) setError(`Unable to load ${kind}. Please try again.`);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [search, page, retry, retainer, kind]);

  const buttonClass = "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <section aria-labelledby={sectionId} className="space-y-5 pt-8 sm:pt-10">
      <div>
        <h3 id={sectionId} className="text-lg font-medium text-gray-900">{retainer ? "Add Retainer Services" : "Add Services"} {required && <span className="text-sm text-red-600">(required)</span>}</h3>
        <p className="mt-1 text-sm text-gray-500">Choose the {kind} that apply to this customer. At least one regular service or retainer service must be mapped before creating the customer.</p>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="min-w-0 rounded-md border border-gray-200 p-4">
          <label htmlFor={searchId} className="block text-sm font-medium text-gray-700">Search {kind}</label>
          <input id={searchId} type="search" value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            className="mt-2 block w-full rounded-md border-gray-300 text-sm focus:border-red-500 focus:ring-red-500" />
          {loading ? <p role="status" className="mt-4 text-sm text-gray-500">Loading {kind}…</p> : error ? (
            <div className="mt-4"><p role="alert" className="mb-2 text-sm text-red-600">{error}</p><button type="button" className={buttonClass} onClick={() => setRetry(retry + 1)}>Retry {kind}</button></div>
          ) : (
            <>
              <div className="mt-3 max-h-64 overflow-y-auto">
                {services.length === 0 && <p className="text-sm text-gray-500">No {kind} found.</p>}
                {services.map((service) => (
                  <label key={service.id} className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-3 text-sm hover:bg-gray-50">
                    <input type="checkbox" checked={selected.some((item) => item.id === service.id)}
                      onChange={(event) => onChange(event.target.checked ? [...selected, service] : selected.filter((item) => item.id !== service.id))}
                      className="mt-0.5 rounded border-gray-300 text-red-600 focus:ring-red-500" />
                    <span className="min-w-0 break-words">{service.name}</span>
                  </label>
                ))}
              </div>
              {(page > 1 || hasNext) && <div className="mt-3 flex items-center justify-between gap-2">
                <button type="button" className={buttonClass} disabled={page === 1} onClick={() => setPage(page - 1)}>Previous {kind}</button>
                <span className="text-sm text-gray-500">Page {page}</span>
                <button type="button" className={buttonClass} disabled={!hasNext} onClick={() => setPage(page + 1)}>Next {kind}</button>
              </div>}
            </>
          )}
        </div>
        <div className="min-w-0 rounded-md border border-gray-200 p-4">
          <h4 className="text-sm font-medium text-gray-700">Mapped {kind} ({selected.length})</h4>
          {selected.length === 0 && <p className="mt-4 text-sm text-gray-500">No {kind} selected.</p>}
          <ul className="mt-3 max-h-80 divide-y divide-gray-200 overflow-y-auto">
            {selected.map((service) => <li key={service.id} className="flex items-center justify-between gap-3 py-3 text-sm">
              <span className="min-w-0 break-words">{service.name}</span>
              <button type="button" className={buttonClass} aria-label={`Remove ${service.name}`} onClick={() => onChange(selected.filter((item) => item.id !== service.id))}>Remove</button>
            </li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}
